import React, { useEffect, useState, useRef } from "react";
import { MdFilterAlt } from "react-icons/md";
import { Link } from "react-router-dom";
import AllRooms from "./AllRooms";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  getRoomById,
  deleteRoom,
  getAllHotels,
} from "../../../api/owner/hotels/rooms";
import Pagination from "../../../components/common/Pagination";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { useTranslation } from "../../../hooks/useTranslation";

const RoomListing = () => {
  const { t } = useTranslation();
  const [activeTab1, setActiveTab1] = useState("All-Rooms");
  const [status, setStatus] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const filterRef = useRef(null);

  const limit = 5;
  
  // Map active tab to status filter
  const getStatusFilter = () => {
    if (activeTab1 === "Available-Rooms") {
      return "available";
    } else if (activeTab1 === "Booked-Rooms") {
      return "booked";
    }
    return ""; // All rooms - no filter
  };

  // Map filter dropdown value to backend value
  const getVerificationStatus = () => {
    if (status === t("roomListing").filters.statuses.pending) {
      return "pending";
    } else if (status === t("roomListing").filters.statuses.approved) {
      return "approved";
    }
    return ""; // No filter
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["rooms", page, selectedHotelId, searchQuery, activeTab1, status],
    queryFn: () =>
      getRoomById({
        hotelId: selectedHotelId,
        page,
        limit,
        search: searchQuery,
        status: getStatusFilter(),
        verificationStatus: getVerificationStatus(),
      }),
    enabled: !!selectedHotelId,
    keepPreviousData: true,
  });

  const deleteRoomMutation = useMutation({
    mutationFn: (roomId) => deleteRoom(roomId),
    onSuccess: () => {
      queryClient.invalidateQueries(["rooms", page]);
    },
  });

  const { data: hotelData } = useQuery({
    queryKey: ["hotels-listing"],
    queryFn: getAllHotels,
  });

  const hotels = hotelData?.data?.hotels || [];
  const rooms = data?.data?.rooms || [];
  const pagination = data?.data?.pagination || {};

  useEffect(() => {
    if (hotels.length > 0) {
      setSelectedHotelId(hotels[0]._id);
    }
  }, [hotels]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const tabs = [
    { key: "All-Rooms", label: t("roomListing").tabs.allRooms },
    { key: "Available-Rooms", label: t("roomListing").tabs.availableRooms },
    { key: "Booked-Rooms", label: t("roomListing").tabs.bookedRooms },
  ];

  const statuses = [
    t("roomListing").filters.statuses.pending,
    t("roomListing").filters.statuses.approved,
  ];

  const handleFilterChange = (value) => {
    setStatus(value);
    setIsOpen(false);
    setPage(1); // Reset to first page when changing filter
  };

  const handleDeleteRoom = (roomId) => {
    Swal.fire({
      title: t("roomListing").deleteConfirm.title,
      text: t("roomListing").deleteConfirm.text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("roomListing").deleteConfirm.confirm,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteRoomMutation.mutate(roomId, {
          onSuccess: () => {
            toast.success(t("roomListing").toastMessages.deleteSuccess);
          },
          onError: (error) => {
            const errorMessage =
              error?.response?.data?.message ||
              t("roomListing").toastMessages.deleteError;
            toast.error(errorMessage);
          },
        });
      }
    });
  };

  return (
    <div>
      <h2 className="text-[28px] font-semibold text-black mt-[10px]">
        {t("roomListing").title}
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto]">
        <div>
          <div className="flex flex-wrap gap-4 justify-between items-center mt-[21px]">
            <ul className="flex overflow-auto">
              {tabs.map((tab) => (
                <li key={tab.key}>
                  <button
                    className={`px-[16px] py-[8px] inline-flex items-center 2xl:text-lg text-base font-normal gap-[3px] text-[#18181B] hover:text-[#4CAF50] whitespace-nowrap ${
                      activeTab1 === tab.key
                        ? "!text-[#4CAF50] border-b border-[#4CAF50] font-semibold"
                        : "hover:text-[#4CAF50]"
                    }`}
                    onClick={() => {
                      setActiveTab1(tab.key);
                      setPage(1); // Reset to first page when changing tabs
                    }}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            <div className="flex gap-[10px] items-center ">
              <select
                value={selectedHotelId}
                onChange={(e) => setSelectedHotelId(e.target.value)}
                className="py-[9px] px-[10px] bg-white text-[17px] font-medium rounded-[2px] border border-[#DDDDDD] w-full min-w-[200px]"
              >
                <option value="">Select Hotel</option>
                {hotels.map((hotel) => (
                  <option key={hotel._id} value={hotel._id}>
                    {hotel.name}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <div className="relative" ref={filterRef}>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="py-[9px] px-[35px] bg-white text-[17px] font-medium flex items-center gap-[6px] rounded-[2px] border border-[#DDDDDD] w-full"
                >
                  <MdFilterAlt /> {status || t("roomListing").filters.filter}
                </button>
                {isOpen && (
                  <div className="absolute z-10 mt-2 w-36 bg-white border rounded shadow text-sm font-medium">
                    <div
                      onClick={() => handleFilterChange("")}
                      className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b"
                    >
                      {t("roomListing").filters.filter}
                    </div>
                    {statuses.map((item) => (
                      <div
                        key={item}
                        onClick={() => handleFilterChange(item)}
                        className={`px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer ${
                          status === item ? "bg-gray-50 font-semibold" : ""
                        }`}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/addnewroom"
                className="w-full min-w-[220px] rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-semibold 2xl:px-[19px] p-[9px] green-shadow hover:bg-[#209b25]"
              >
                {t("roomListing").buttons.addNewRoom}
              </Link>
            </div>
          </div>

          <div className="mt-[22px]">
            <AllRooms
              rooms={rooms}
              pagination={pagination}
              isLoading={isLoading}
              handleDeleteRoom={handleDeleteRoom}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />

            {rooms.length > 0 && (
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                setPage={setPage}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomListing;
